import { useCallback, useEffect, useState } from 'react';

import { Box, Group, Radio, SimpleGrid } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { InputLabel, NumberInput, Select, TextInput, YesNoRadioButton } from 'components';
import { CreditOptionsResponse, CreditRequestForm, CreditResponse } from 'types';

import CreditRequestExpenses from './expenses';

type HousingExpense = 'MORTGAGE' | 'RENT';

interface CreditRequestOutcomesProps {
  credit: CreditResponse;
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
}

const CreditRequestOutcomes = ({ credit, form, options }: CreditRequestOutcomesProps) => {
  const { t } = useTranslation();

  const [housingExpense, setHousingExpense] = useState<HousingExpense | undefined>(
    form.getValues().mortgage ? 'MORTGAGE' : form.getValues().rent ? 'RENT' : undefined,
  );

  const changeHousingExpense = useCallback(
    (val: HousingExpense) => {
      setHousingExpense(val);
      if (val === 'MORTGAGE') {
        form.setFieldValue('mortgage', true);
        form.setFieldValue('rent', undefined);
        form.setFieldValue('rentAmount', undefined);
      } else if (val === 'RENT') {
        form.setFieldValue('rent', true);
        form.setFieldValue('mortgage', undefined);
        form.setFieldValue('mortgageAmount', undefined);
      }
    },
    [form],
  );

  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }
    if (formValues.additionalExpenses === false) {
      //not using visibleFields here because expenses is an array
      form.setFieldValue('expenses', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getValues().additionalExpenses]);

  return (
    <Box>
      <Box bg={'var(--mantine-color-gray-1)'} p={'md'}>
        <SimpleGrid cols={4}>
          <Select
            label={t('credit.houseSituation.label')}
            data={options['houseSituation'].map((el) => ({
              value: el,
              label: t(`credit.houseSituation.options.${el}`),
            }))}
            key={form.key('houseSituation')}
            {...form.getInputProps('houseSituation')}
            clearable={true}
          />
          <Box>
            <InputLabel label={t('credit.mortgageOrRent.label')} />
            <Group>
              <Radio
                checked={housingExpense === 'MORTGAGE'}
                onChange={() => changeHousingExpense('MORTGAGE')}
                label={t('credit.mortgageOrRent.options.MORTGAGE')}
              />
              <Radio
                checked={housingExpense === 'RENT'}
                onChange={() => changeHousingExpense('RENT')}
                label={t('credit.mortgageOrRent.options.RENT')}
              />
            </Group>
          </Box>
          {housingExpense === 'MORTGAGE' && (
            <NumberInput
              label={t('credit.mortgageAmount.label')}
              key={form.key('mortgageAmount')}
              {...form.getInputProps('mortgageAmount')}
              min={0}
            />
          )}
          {housingExpense === 'RENT' && (
            <NumberInput
              label={t('credit.rentAmount.label')}
              key={form.key('rentAmount')}
              {...form.getInputProps('rentAmount')}
              min={options['rentAmount']?.min}
              max={options['rentAmount']?.max}
            />
          )}
        </SimpleGrid>
      </Box>

      <Box p={'md'} mt={'sm'}>
        <SimpleGrid cols={4}>
          <NumberInput
            label={t('credit.healthInsuranceAmount.label')}
            key={form.key('healthInsuranceAmount')}
            {...form.getInputProps('healthInsuranceAmount')}
            min={0}
          />
          <YesNoRadioButton
            label={t('credit.healthInsuranceDiscount.label')}
            name={'healthInsuranceDiscount'}
            form={form}
            t={t}
          />
          <YesNoRadioButton
            label={t('credit.enforcedCollection.label')}
            name={'enforcedCollection'}
            form={form}
            t={t}
          />
          <YesNoRadioButton
            label={t(`credit.expenseType.anotherExpense.label`)}
            name={'additionalExpenses'}
            form={form}
            t={t}
          />
        </SimpleGrid>
      </Box>

      {form.getValues().additionalExpenses && (
        <CreditRequestExpenses credit={credit} form={form} options={options} />
      )}

      <Box bg={'var(--mantine-color-gray-1)'} p={'md'} mt={'sm'}>
        <SimpleGrid cols={4}>
          <TextInput
            label={t('credit.comment.singular')}
            key={form.key('comment4')}
            {...form.getInputProps('comment4')}
          />
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default CreditRequestOutcomes;
